import { useEffect, useId, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useSubmit } from '../../hooks/useSubmit';
import { stopKuur } from '../adminApi';
import { useAdmin } from '../AdminContext';
import { Field } from '../ui/Field';
import { NumberInput } from '../ui/NumberInput';
import { AutoTextarea } from '../ui/AutoTextarea';
import { btnPrimary, btnSecondary, inputCls } from '../ui/styles';
import { Spinner } from '../../ui/Spinner';
import { useRequestId } from '../../hooks/useRequestId';
import { money, toCents } from '../../core/format/currency';
import { today } from '../../core/format/dates';
import { kuurRefund, kuurStopProblem, suggestedRefundCents } from '../../core/rules/kuur';
import { refundMethodLabel } from '../kassa/kassaLabels';
import { printKuurCreditNote } from '../kassa/kuurCreditNote';
import type { Course } from '../../core/types/account';
import type { Treatment } from '../../core/types/catalog';
import type { KuurRefundMethod } from '../../core/types/kassa';
import type { CustomerFileControl } from './useCustomerFile';
type Props = {
  ctl: CustomerFileControl;
  course: Course & {
    prepaid: NonNullable<Course['prepaid']>;
  };
  treatment: Treatment | undefined;
  onClose: () => void;
};
const methods: {
  value: KuurRefundMethod;
  hint: string;
}[] = [{
  value: 'pin',
  hint: 'Via de pinautomaat terug; telt in de kassa van vandaag.'
}, {
  value: 'contant',
  hint: 'Uit de la; telt in de kassa van vandaag.'
}, {
  value: 'tegoed',
  hint: 'Op haar tegoed, te gebruiken aan de kassa. Er gaat geen geld de deur uit.'
}];

/**
 * Een vooruitbetaalde kuur stoppen en terugbetalen (docs/KASSA-ABONNEMENTEN.md §4.4). Het voorstel
 * komt uit `kuurRefund`; een ander bedrag mag met een reden. Eerst invullen, dan één keer bevestigen.
 * Eén `requestId` per poging, zodat een herhaling na een verloren antwoord nooit twee keer uitbetaalt.
 */
export function KuurStopForm({
  ctl,
  course,
  treatment,
  onClose
}: Props) {
  const amountId = useId();
  const errorId = useId();
  const {
    site
  } = useAdmin();
  const now = today();
  const suggestion = kuurRefund(course, treatment?.price ?? null, now);
  const suggestedCents = suggestedRefundCents(course, treatment?.price ?? null, now);
  // Zelfde omrekening als de server (demo/kassaBackend): anders kan "hooguit wat betaald is" een cent schelen.
  const paidCents = toCents(course.prepaid.paid);
  const [refundCents, setRefundCents] = useState<number | undefined>(suggestedCents);
  const [method, setMethod] = useState<KuurRefundMethod>('pin');
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const {
    submit,
    submitting,
    error,
    fieldErrors
  } = useSubmit(stopKuur);
  const problem = kuurStopProblem({
    refundCents,
    suggestedCents,
    paidCents,
    reason
  });
  const deviates = refundCents !== undefined && refundCents !== suggestedCents;
  const attempt = `${refundCents}|${method}|${reason.trim()}`;
  const requestId = useRequestId(attempt);
  const confirmRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (confirming) confirmRef.current?.focus();
  }, [confirming]);
  const name = treatment?.name ?? course.treatmentSlug;
  const amountLabel = money(refundCents ?? 0);
  const send = async () => {
    if (problem || refundCents === undefined) return;
    const stop = await submit(ctl.file.account.customer.id, {
      requestId,
      refundCents,
      method,
      reason: reason.trim()
    });
    if (!stop) {
      setConfirming(false);
      requestAnimationFrame(() => document.getElementById(`${errorId}-form`)?.focus());
      return;
    }
    onClose();
    ctl.kuurStopped();
    toast.success(`Kuur gestopt · creditnota ${stop.number}`, {
      description: stop.refundCents === 0 ? 'Er gaat niets terug.' : stop.method === 'tegoed' ? `${money(stop.refundCents)} staat op haar tegoed.` : `Betaal ${money(stop.refundCents)} terug (${refundMethodLabel[stop.method].toLowerCase()}); het staat in de kassa van vandaag.`,
      duration: 12_000,
      action: {
        label: 'Creditnota',
        onClick: () => {
          void printKuurCreditNote(stop, site.salon).then(ok => {
            if (!ok) toast.error('Creditnota openen lukte niet', {
              description: 'Probeer het opnieuw of gebruik een andere browser.'
            });
          });
        }
      }
    });
  };
  const amountError = fieldErrors.refundCents;
  const reasonError = fieldErrors.reason;
  const methodError = fieldErrors.method;
  const formError = error && !amountError && !reasonError && !methodError ? error : null;
  return <div className="mt-4 rounded-lg border border-line bg-shell p-4 sm:p-5" data-id="element-1388" data-mp="1jj1qe5del5tp">
      <h3 className="text-[15px] font-medium text-ink" data-id="element-1389" data-mp="1xht0zht249d3">Kuur stoppen en terugbetalen</h3>
      <p className="mt-0.5 text-[13px] text-muted" data-id="element-1390" data-mp="1sl2lowp2thsq">
        {name} · {course.done} van {course.total} gedaan · vooruit betaald {money(paidCents)}
      </p>

      {!confirming ? <div className="mt-4 space-y-4" data-id="element-1391" data-mp="3ubw2fnasw52b">
          <div data-id="element-1392" data-mp="3ud77rrjw9kvj">
            <Field label="Terug aan de klant (€)" data-id="element-1393" data-mp="3udgw7apajjz4">
              <NumberInput decimals allowEmpty value={refundCents === undefined ? undefined : refundCents / 100} onChange={v => setRefundCents(v === undefined ? undefined : Math.round(v * 100))} className="w-36" data-id="element-1394" data-mp="29ud6oi8r16ft" />
            </Field>
            <p className="mt-1.5 text-[12px] text-muted" data-id="element-1395" data-mp="250shnfp4vmsr">
              Voorstel {money(suggestedCents)}: {suggestion.explanation}
              {deviates && <>
                  {' '}
                  <button type="button" onClick={() => setRefundCents(suggestedCents)} className="text-ink underline underline-offset-2 hover:text-clay" data-id="element-1396" data-mp="1vzuawpnb916h">
                    Terug naar het voorstel
                  </button>
                </>}
            </p>
            {(amountError || problem?.field === 'refundCents') && <p role="alert" className="mt-1 text-[12px] text-clay" data-id="element-1397" data-mp="3giabnngwx823">
                {amountError ?? problem?.message}
              </p>}
          </div>

          {(refundCents ?? 0) > 0 && <fieldset data-id="element-1398" data-mp="o9yo02qopzj8">
              <legend className="text-[13px] font-medium text-ink" data-id="element-1399" data-mp="o9ggub2hi0wj">Hoe gaat het terug?</legend>
              <div className="mt-2 space-y-2" data-id="element-1400" data-mp="k9fvs7kvqrgi">
                {methods.map(m => <label key={m.value} className="flex items-start gap-2.5 text-[14px] text-ink" data-id="element-1401" data-mp="falexb6h3cz1">
                    <input type="radio" name={`${amountId}-method`} checked={method === m.value} onChange={() => setMethod(m.value)} className="mt-0.5 h-4 w-4 accent-clay" data-id="element-1402" data-mp="1byrbdat8qz28" />
                    <span data-id="element-1403" data-mp="y7cq0xbv2ls0">
                      {refundMethodLabel[m.value]}
                      <span className="block text-[12px] text-muted" data-id="element-1404" data-mp="1vobgaa67jn4z">{m.hint}</span>
                    </span>
                  </label>)}
              </div>
              {methodError && <p role="alert" className="mt-1.5 text-[12px] text-clay" data-id="element-1405" data-mp="1y916ubvf2q5w">
                  {methodError}
                </p>}
            </fieldset>}

          <Field label={deviates ? 'Reden (verplicht)' : 'Toelichting (optioneel)'} hint={deviates ? 'Je wijkt af van het voorstel. De reden staat op de creditnota.' : 'Staat op de creditnota, bijv. waarom ze stopt.'} data-id="element-1406" data-mp="286nmx1ogcnrg">
            <AutoTextarea value={reason} onChange={e => setReason(e.target.value)} minRows={2} maxLength={300} className={inputCls} aria-invalid={reasonError ? true : undefined} aria-describedby={reasonError ? errorId : undefined} data-id="element-1407" data-mp="2h6kfvfhvk8sb" />
          </Field>
          {reasonError && <p id={errorId} role="alert" className="-mt-2 text-[12px] text-clay" data-id="element-1408" data-mp="2zz69dt5b7n9q">
              {reasonError}
            </p>}
          {formError && <p id={`${errorId}-form`} tabIndex={-1} role="alert" className="rounded-md bg-blush/50 px-3 py-2 text-[13px] text-ink outline-none" data-id="element-1409" data-mp="3q73j7z0984jf">
              {formError}
            </p>}

          <div className="flex flex-wrap gap-2" data-id="element-1410" data-mp="40ya7k5fesap">
            <button type="button" onClick={() => setConfirming(true)} disabled={problem !== null} className={btnPrimary} data-id="element-1411" data-mp="3zn5ni45pgij">
              {(refundCents ?? 0) > 0 ? `Stoppen en ${amountLabel} terugbetalen…` : 'Stoppen zonder terugbetaling…'}
            </button>
            <button type="button" onClick={onClose} className={btnSecondary} data-id="element-1412" data-mp="32itvgrjhd6pn">
              Niet stoppen
            </button>
          </div>
          {problem?.field === 'reason' && <p className="text-[12px] text-muted" data-id="element-1413" data-mp="3cgi14cimjyml">Vul eerst een reden in om verder te gaan.</p>}
        </div> : <div className="mt-4 rounded-md bg-sand px-3 py-3 text-[14px]" role="alertdialog" aria-label="Kuur stoppen bevestigen" aria-describedby={`${errorId}-confirm`} data-id="element-1414" data-mp="2opuesm7yp6gi">
          <p id={`${errorId}-confirm`} className="text-ink" data-id="element-1415" data-mp="z9ick8p1jh3c">
            De kuur van {ctl.file.account.customer.firstName} stoppen
            {(refundCents ?? 0) > 0 ? <>
                {' '}
                en <strong className="font-medium" data-id="element-1416" data-mp="pbui7jvckblp">{amountLabel}</strong> terugbetalen ({refundMethodLabel[method].toLowerCase()})
              </> : null}
            ? De kuur verdwijnt uit Mijn omgeving en er komt een creditnota. Dit kan niet ongedaan worden gemaakt.
          </p>
          <div className="mt-3 flex flex-wrap gap-2" data-id="element-1417" data-mp="1myukz6cjnl4l">
            <button ref={confirmRef} type="button" onClick={() => void send()} disabled={submitting} aria-busy={submitting} className={btnPrimary} data-id="element-1418" data-mp="1mykuziri1dyw">
              {submitting && <Spinner data-id="element-1419" data-mp="1d05avzqw34yw" />}
              {submitting ? 'Bezig…' : 'Ja, kuur stoppen'}
            </button>
            <button type="button" onClick={() => setConfirming(false)} disabled={submitting} className={btnSecondary} data-id="element-1420" data-mp="3tohj40goejjm">
              Terug
            </button>
          </div>
        </div>}
    </div>;
}